import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [passwordRecovery, setPasswordRecovery] = useState(false);
  const [forcePasswordSetup, setForcePasswordSetup] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchProfile = async (userId) => {
    const { data, error } = await supabase
      .from('profiles')
      .select(
        'id, full_name, role, email, email_notifications_enabled, password_configured'
      )
      .eq('id', userId)
      .single();

    if (error) {
      // Older DBs may not have newer preference columns yet.
      if (
        /email_notifications_enabled|password_configured|schema cache|column/i.test(
          error.message || ''
        )
      ) {
        const { data: fallback, error: fallbackError } = await supabase
          .from('profiles')
          .select('id, full_name, role, email')
          .eq('id', userId)
          .single();
        if (fallbackError) {
          // eslint-disable-next-line no-console
          console.error('Failed to load profile:', fallbackError.message);
          return null;
        }
        return {
          ...fallback,
          email_notifications_enabled: true,
          password_configured: true,
        };
      }
      // eslint-disable-next-line no-console
      console.error('Failed to load profile:', error.message);
      return null;
    }
    return {
      ...data,
      email_notifications_enabled: data.email_notifications_enabled !== false,
      // Missing/null treated as configured so existing accounts keep access.
      password_configured: data.password_configured !== false,
    };
  };

  useEffect(() => {
    let mounted = true;

    const init = async () => {
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      if (!mounted) return;

      setSession(currentSession);

      if (currentSession?.user) {
        const p = await fetchProfile(currentSession.user.id);
        if (mounted) setProfile(p);
      }

      if (mounted) setLoading(false);
    };

    init();

    // Invite / recovery links often include type=invite|recovery in the hash.
    if (typeof window !== 'undefined') {
      const hash = window.location.hash?.replace(/^#/, '') || '';
      const params = new URLSearchParams(hash);
      const linkType = (params.get('type') || '').toLowerCase();
      if (linkType === 'invite' || linkType === 'recovery') {
        setForcePasswordSetup(true);
      }
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, nextSession) => {
        if (event === 'PASSWORD_RECOVERY') {
          setPasswordRecovery(true);
          setForcePasswordSetup(true);
        }
        setSession(nextSession);
        if (nextSession?.user) {
          const p = await fetchProfile(nextSession.user.id);
          setProfile(p);
          if (p && p.password_configured !== false && event !== 'PASSWORD_RECOVERY') {
            setForcePasswordSetup(false);
          }
        } else {
          setProfile(null);
          setForcePasswordSetup(false);
        }
        setLoading(false);
      }
    );

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  };

  const signInWithGoogle = async () => {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.origin,
      },
    });
    if (error) throw error;
    return data;
  };

  const formatAuthError = (error) => {
    if (!error) return 'Authentication failed';
    if (typeof error === 'string' && error.trim()) return error.trim();
    const message = String(
      error.message ||
        error.error_description ||
        error.error ||
        error.msg ||
        ''
    ).trim();
    if (message && message !== '{}' && message !== '[object Object]') {
      return message;
    }
    const status = error.status || error.statusCode;
    if (status) {
      return `Authentication failed (code ${status}). Check Supabase Auth SMTP settings and that this email has an account.`;
    }
    return 'Authentication failed. If this was a password reset, check Supabase → Authentication → Emails → SMTP Settings (sender must be allowed for your Google account).';
  };

  const requestPasswordReset = async (email) => {
    const normalized = String(email || '').trim().toLowerCase();
    if (!normalized) {
      throw new Error('Enter the email address for your Tikix PMS Tracker account.');
    }

    const { data, error } = await supabase.auth.resetPasswordForEmail(normalized, {
      redirectTo: `${window.location.origin}/`,
    });
    if (error) {
      const friendly = new Error(formatAuthError(error));
      friendly.cause = error;
      throw friendly;
    }
    return data;
  };

  const updatePassword = async (password) => {
    const { data, error } = await supabase.auth.updateUser({ password });
    if (error) throw error;

    // Mark invitees / recovery users as fully configured for future logins.
    const { error: markError } = await supabase.rpc('mark_password_configured');
    if (markError) {
      // Fallback if RPC migration is not applied yet.
      if (session?.user?.id) {
        await supabase
          .from('profiles')
          .update({ password_configured: true })
          .eq('id', session.user.id);
      }
    }

    setProfile((prev) =>
      prev ? { ...prev, password_configured: true } : prev
    );
    setPasswordRecovery(false);
    setForcePasswordSetup(false);
    return data;
  };

  const signUp = async (email, password, fullName) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/`,
        data: {
          full_name: fullName,
        },
      },
    });
    if (error) throw error;
    return data;
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setProfile(null);
    setSession(null);
    setPasswordRecovery(false);
    setForcePasswordSetup(false);
  };

  const updateEmailNotificationsEnabled = async (enabled) => {
    if (!session?.user?.id) {
      throw new Error('You must be signed in to update notification settings.');
    }
    const next = Boolean(enabled);
    const { data, error } = await supabase.rpc('set_email_notifications_enabled', {
      enabled: next,
    });

    if (error) {
      if (
        /set_email_notifications_enabled|email_notifications_enabled|schema cache|function/i.test(
          error.message || ''
        )
      ) {
        throw new Error(
          'Notification preference needs a database update. Run the email_notifications_enabled migration, then try again.'
        );
      }
      throw error;
    }

    setProfile((prev) =>
      prev
        ? {
            ...prev,
            email_notifications_enabled:
              typeof data === 'boolean' ? data : next,
          }
        : prev
    );
    return typeof data === 'boolean' ? data : next;
  };

  const currentUser = profile
    ? {
        id: profile.id,
        name: profile.full_name || profile.email || 'User',
        email: profile.email,
        role: ['admin', 'client'].includes(profile.role) ? profile.role : 'employee',
        emailNotificationsEnabled: profile.email_notifications_enabled !== false,
        passwordConfigured: profile.password_configured !== false,
      }
    : null;

  const needsPasswordSetup =
    Boolean(session?.user) &&
    (passwordRecovery ||
      forcePasswordSetup ||
      (profile && profile.password_configured === false));

  return (
    <AuthContext.Provider
      value={{
        session,
        profile,
        currentUser,
        loading,
        signIn,
        signInWithGoogle,
        requestPasswordReset,
        updatePassword,
        passwordRecovery,
        needsPasswordSetup,
        signUp,
        signOut,
        updateEmailNotificationsEnabled,
        isAuthenticated: Boolean(session?.user),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
